import React, { useState, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import "./ImageUploader.css";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CircularProgress from "@mui/material/CircularProgress";
import ImageIcon from "@mui/icons-material/Image";
import SdStorageIcon from "@mui/icons-material/SdStorage";
import * as API from "../../api.js";
import AnimatedModal from "../AnimatedModal.js";
import { CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB default chunk size

export default function ImageUploader({
  upload_type,
  onFilesUpload,
  multiple,
  collection,
  serviceName,
  imageType,
  planId,
  floorId,
}) {
  const [files, setFiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({ type: "", message: "" });
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [isCancel, setIsCancel] = useState(false);

  const filesRef = useRef([]);
  filesRef.current = files;

  // Calculate overall progress whenever files change
  useEffect(() => {
    if (files.length > 0) {
      const total = files.length;
      const completed = files.filter((f) => f.status === "done").length;
      const inProgress = files.filter((f) => f.status === "uploading").length;

      // Calculate progress for in-progress files
      let progressSum = files.reduce((sum, file) => sum + file.progress, 0);
      const overall = Math.round((progressSum / (total * 100)) * 100);

      setOverallProgress(overall);
      setUploadedCount(completed);
      setTotalFiles(total);

      // Show progress modal when upload starts
      if (inProgress > 0 && !showProgressModal) {
        setShowProgressModal(true);
      }

      // Hide progress modal when all files are done
      // if (completed === total && showProgressModal) {
      // setTimeout(() => {
      //   setShowProgressModal(false);
      // }, 1000);
      // }
    }
  }, [files, showProgressModal]);

  // Queue processing
  const processQueue = () => {
    const isUploading = filesRef.current.some((f) => f.status === "uploading");
    if (!isUploading) {
      const nextFile = filesRef.current.find((f) => f.status === "queued");
      if (nextFile) startChunkUpload(nextFile.id);
    }
  };

  useEffect(() => {
    processQueue();
  }, [files]);

  // Show success/error modal after all uploads are done
  useEffect(() => {
    if (files.length > 0) {
      const allCompleted = files.every(
        (f) => f.status === "done" || f.status === "error"
      );
      if (allCompleted) {
        const hasError = files.some((f) => f.status === "error");
        setModalContent({
          type: hasError ? "error" : "success",
          message: hasError
            ? "Some files failed to upload. Please try again."
            : "Upload Complete!",
        });
        setShowModal(true);
      }
    }
  }, [files]);

  // File selection handler
  const onFilesSelected = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    const filteredFiles = selectedFiles.filter((file) => {
      const ext = file.name.toLowerCase();
      return (
        ext.endsWith(".tif") ||
        ext.endsWith(".tiff") ||
        ext.endsWith(".png") ||
        ext.endsWith(".jpeg") ||
        ext.endsWith(".jpg")
      );
    });

    const newFiles = filteredFiles.map((file) => ({
      id: uuidv4(),
      file,
      progress: 0,
      status: "queued",
    }));

    setFiles((prev) => [...prev, ...newFiles]);
    setShowProgressModal(true);
    event.target.value = null;
  };

  const startChunkUpload = async (fileId) => {
    if (isCancel) return;

    const fileObj = filesRef.current.find((f) => f.id === fileId);
    if (!fileObj) return;

    let currentChunkIndex = 0;
    const totalChunks = Math.ceil(fileObj.file.size / CHUNK_SIZE);

    const uploadChunk = async () => {
      if (isCancel) return;

      const start = currentChunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, fileObj.file.size);
      const chunk = fileObj.file.slice(start, end);

      const formData = new FormData();
      formData.append("file", chunk);
      formData.append("name", fileObj.file.name);
      formData.append("size", fileObj.file.size);
      formData.append("currentChunkIndex", currentChunkIndex);
      formData.append("totalChunks", totalChunks);
      formData.append("serviceName", "PicStork");
      formData.append("collection_id", collection);
      formData.append("imageType", imageType || "normal");
      if (floorId) formData.append("floorId", floorId);
      if (planId) formData.append("planId", planId);

      try {
        const response = await API.uploadImages(formData);
        if (response.status === 200) {
          currentChunkIndex++;
          const progress = Math.min(
            100,
            Math.round((currentChunkIndex / totalChunks) * 100)
          );
          setFiles((prev) =>
            prev.map((f) =>
              f.id === fileId
                ? {
                    ...f,
                    progress,
                    status:
                      currentChunkIndex === totalChunks ? "done" : "uploading",
                  }
                : f
            )
          );

          if (currentChunkIndex < totalChunks) {
            await uploadChunk();
          } else {
            // finished this file
            processQueue();
          }
        } else {
          setFiles((prev) =>
            prev.map((f) => (f.id === fileId ? { ...f, status: "error" } : f))
          );
          processQueue();
        }
      } catch (error) {
        setFiles((prev) =>
          prev.map((f) => (f.id === fileId ? { ...f, status: "error" } : f))
        );
        processQueue();
      }
    };

    // Update status to uploading
    setFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, status: "uploading" } : f))
    );

    uploadChunk();
  };

  const cancelUpload = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    processQueue();
  };

  const cancelAllUploads = () => {
    setIsCancel(true);
    setFiles([]);
    setShowProgressModal(false);
  };

  const handleModalClose = () => {
    setShowProgressModal(false);
    const hasError = filesRef.current.some((f) => f.status === "error");
    setFiles([]);
    if (typeof onFilesUpload === "function") {
      try {
        onFilesUpload(hasError);
      } catch (e) {
        console.error("onFilesUpload callback threw:", e);
      }
    }
  };

  return (
    <>
      <AnimatedModal
        isOpen={showProgressModal}
        onRequestClose={handleModalClose}
        height="350px"
        width="450px"
      >
        <div style={{ padding: "20px" }}>
          <div
            style={{
              textAlign: "center",
              marginTop: "15px",
              fontSize: "18px",
              fontWeight: "bold",
            }}
          >
            {overallProgress === 100
              ? "Upload Completed!"
              : "Uploading Images..."}
          </div>
          <div
            style={{ textAlign: "center", marginTop: "4%", marginBottom: "2%" }}
          >
            <div style={{ width: 175, height: 175, margin: "auto" }}>
              <CircularProgressbar
                value={overallProgress}
                text={`${uploadedCount}/${totalFiles}`}
                styles={{
                  text: {
                    fill: "#2989cf",
                    fontSize:
                      totalFiles > 99999
                        ? "9px"
                        : totalFiles > 9999
                        ? "11px"
                        : totalFiles > 999
                        ? "13px"
                        : "15px",
                  },
                }}
              />
            </div>
          </div>
          <div style={{ textAlign: "right", marginTop: "20px" }}>
            <button
              style={{
                backgroundColor:
                  overallProgress === 100 ? "#3b82f6" : "#dc2626",
                color: "white",
                padding: "8px 16px",
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
              }}
              onClick={
                overallProgress === 100
                  ? () => handleModalClose()
                  : cancelAllUploads
              }
            >
              {overallProgress === 100 ? "Close" : "Cancel"}
            </button>
          </div>
        </div>
      </AnimatedModal>

      <div className="uploader-container">
        <div className="uploader-dropzone">
          <input
            type="file"
            multiple={multiple !== false}
            onChange={onFilesSelected}
            accept=".tif,.tiff,.png,.jpeg,.jpg"
            style={{ display: "none" }}
            id="hiddenFileInput"
          />
          <label htmlFor="hiddenFileInput" className="upload-label">
            <FileUploadOutlinedIcon className="uploader-icon" />
            <p className="uploader-text">
              Drag and drop files here to upload, or{" "}
              <span>click to select files</span>
            </p>
          </label>

          <div className="file-list">
            {files.map((file) => (
              <div key={file.id} className="file-item">
                <div className="file-item-header">
                  <div>
                    <p className="file-name">{file.file.name}</p>
                    <p className="file-size">
                      {(file.file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                  <div className="file-status-actions">
                    {file.status === "queued" && (
                      <AccessTimeIcon
                        style={{ color: "#3b82f6", fontSize: 18 }}
                      />
                    )}
                    {file.status === "done" && (
                      <CheckCircleIcon
                        style={{ color: "#16a34a", fontSize: 18 }}
                      />
                    )}
                    {file.status === "error" && (
                      <ErrorIcon style={{ color: "#dc2626", fontSize: 18 }} />
                    )}
                    {file.status === "uploading" && (
                      <CircularProgress
                        size={16}
                        style={{ color: "#3b82f6" }}
                      />
                    )}
                    <button
                      onClick={() => cancelUpload(file.id)}
                      className="cancel-btn"
                      title={
                        file.status === "uploading"
                          ? "Cancel Upload"
                          : "Remove File"
                      }
                    >
                      <CloseIcon style={{ fontSize: 18 }} />
                    </button>
                  </div>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress"
                    style={{
                      width: `${file.progress}%`,
                      backgroundColor:
                        file.status === "error"
                          ? "#ef4444"
                          : file.status === "done"
                          ? "#16a34a"
                          : "#3b82f6",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <h3 className="supported-title">Supported Formats</h3>
          <div className="supported-box">
            <div className="supported-row">
              <div className="supported-item">
                <div className="supported-label">
                  <ImageIcon style={{ fontSize: 16, color: "#1F1C45" }} />
                  <span className="supported-text">Image Type</span>
                </div>
                <span className="supported-subtext">.png .jpeg .tif</span>
              </div>
              <div className="supported-item">
                <div className="supported-label">
                  <SdStorageIcon style={{ fontSize: 16, color: "#1F1C45" }} />
                  <span className="supported-text">Image Size</span>
                </div>
                <span
                  className="supported-subtext"
                  style={{ marginLeft: "20px" }}
                >
                  Maximum size 30 MB
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
